import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useEffect } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import currencyFormatter from '@magalu/stereo-ui-utils/currencyFormatter';
import { useInView } from 'react-intersection-observer';
import { useCart } from '@magalu/mixer-graphql';
import { usePublisher } from '@magalu/mixer-publisher';
import { cartRedirect, routePush } from '@magalu/mixer-utils';
import { formatQuantity, handleAddToCart, isCardWithButton } from './QuickClickCard.utils';
import { useInViewConfig } from './constants';
import { CardContent, CardHeader, Container, Image, ImageContainer, ProductTitle, Price, BestPrice, Title } from './QuickClickCard.styles';
import QuickClickCardFooter from './QuickClickCardFooter';
var QuickClickCard = function QuickClickCard(_ref) {
  var button = _ref.button,
    cartExtensions = _ref.cartExtensions,
    cartUrl = _ref.cartUrl,
    clickEvent = _ref.clickEvent,
    designTokenId = _ref.designTokenId,
    image = _ref.image,
    location = _ref.location,
    minimumOrderQuantity = _ref.minimumOrderQuantity,
    path = _ref.path,
    price = _ref.price,
    product = _ref.product,
    productId = _ref.productId,
    productTitle = _ref.productTitle,
    rating = _ref.rating,
    restrictions = _ref.restrictions,
    salesChannelId = _ref.salesChannelId,
    sellerId = _ref.sellerId,
    structure = _ref.structure,
    shippingTag = _ref.shippingTag,
    tagCoupon = _ref.tagCoupon,
    title = _ref.title,
    viewEvent = _ref.viewEvent;
  var _useCart = useCart(),
    _useCart2 = _slicedToArray(_useCart, 1),
    addToCart = _useCart2[0].addToCart;
  var _useInView = useInView(useInViewConfig),
    _useInView2 = _slicedToArray(_useInView, 2),
    ref = _useInView2[0],
    inView = _useInView2[1];
  var _usePublisher = usePublisher(),
    publish = _usePublisher.publish;
  var bestPrice = price.bestPrice;
  var formattedBestPrice = currencyFormatter({
    value: bestPrice
  });
  var hasActionButton = isCardWithButton({
    button: button,
    designTokenId: designTokenId
  });
  var handleClick = function handleClick(event) {
    event.preventDefault();
    clickEvent();
    if (!hasActionButton) {
      routePush({
        path: path,
        spa: false
      });
      return;
    }
    if (button.action.includes('view_cart')) {
      cartRedirect({
        cartUrl: cartUrl
      });
      return;
    }
    if (button.action.includes('add_item_to_cart')) {
      var _structure$cookies;
      handleAddToCart({
        addToCart: addToCart,
        cartExtensions: cartExtensions,
        cartUrl: cartUrl,
        channel: salesChannelId,
        customerId: structure == null ? void 0 : (_structure$cookies = structure.cookies) == null ? void 0 : _structure$cookies.customerId,
        location: location,
        product: product,
        publish: publish,
        quantity: formatQuantity(minimumOrderQuantity),
        sellerId: sellerId
      });
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  };
  useEffect(function () {
    if (inView) viewEvent();
  }, [inView]);
  return React.createElement(Container, {
    lang: "pt-BR",
    "aria-labelledby": "titulo-".concat(productId, " preco-").concat(productId),
    "data-testid": "quickclickcard-".concat(productId),
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    role: "button",
    tabIndex: "0",
    ref: ref
  }, React.createElement(CardHeader, null, React.createElement(Title, {
    as: "h3",
    id: "titulo-".concat(productId)
  }, title), React.createElement(ImageContainer, null, React.createElement(Image, {
    src: image,
    lazy: true,
    alt: "Foto do produto ".concat(productTitle),
    "aria-hidden": "false"
  }))), React.createElement(CardContent, null, React.createElement(ProductTitle, {
    id: "descricao-".concat(productId),
    "data-testid": "quickclickcard-product-title"
  }, productTitle), React.createElement(Price, {
    id: "preco-".concat(productId)
  }, React.createElement(BestPrice, {
    as: "h4",
    "aria-label": "Valor: ".concat(formattedBestPrice)
  }, formattedBestPrice)), React.createElement(QuickClickCardFooter, {
    button: button,
    hasActionButton: hasActionButton,
    rating: rating,
    restrictions: restrictions,
    shippingTag: shippingTag,
    tagCoupon: tagCoupon
  })));
};
process.env.NODE_ENV !== "production" ? QuickClickCard.propTypes = {
  button: shape({
    action: string.isRequired,
    label: string.isRequired
  }),
  cartExtensions: oneOfType([arrayOf(shape({})), bool]),
  cartUrl: shape({}).isRequired,
  clickEvent: func,
  designTokenId: string.isRequired,
  image: string.isRequired,
  location: shape({}),
  minimumOrderQuantity: string,
  path: string.isRequired,
  price: shape({
    bestPrice: string
  }),
  product: shape({}).isRequired,
  productId: string.isRequired,
  productTitle: string.isRequired,
  rating: shape({
    count: number,
    score: number
  }),
  restrictions: shape({}),
  salesChannelId: string,
  sellerId: string.isRequired,
  shippingTag: shape({}),
  structure: shape({
    cookies: shape({
      customerId: string
    })
  }),
  tagCoupon: shape({}),
  title: string.isRequired,
  viewEvent: func
} : void 0;
QuickClickCard.defaultProps = {
  button: null,
  cartExtensions: false,
  clickEvent: function clickEvent() {},
  location: null,
  minimumOrderQuantity: '1',
  price: null,
  rating: null,
  restrictions: null,
  salesChannelId: '',
  shippingTag: {
    complement: null,
    cost: null,
    time: null
  },
  structure: {
    cookies: shape({
      customerId: string
    })
  },
  tagCoupon: null,
  viewEvent: function viewEvent() {}
};
export default QuickClickCard;